import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { FOOTER } from "../mock";
import { useState } from "react";
import { useToast } from "../hooks/use-toast";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    // Lightweight client-only handler — logs to localStorage so the lead
    // isn't lost. Replaced with a real /api/contact endpoint later.
    try {
      const all = JSON.parse(localStorage.getItem("ihub_contact") || "[]");
      all.push({ name, email, message, at: new Date().toISOString() });
      localStorage.setItem("ihub_contact", JSON.stringify(all));
    } catch { /* ignore */ }
    await new Promise((r) => setTimeout(r, 500));
    toast({
      title: "Message received",
      description: "Our investor team will respond within one business day.",
    });
    setName("");
    setEmail("");
    setMessage("");
    setSending(false);
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-[#f7faf9]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#16d196]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#16d196]" />
              Speak to our team
            </div>
            <h2 className="mt-4 text-[32px] sm:text-[40px] lg:text-[52px] font-extrabold leading-[1.05] tracking-[-0.025em] text-[#0d1a2e]">
              Talk to a real{" "}
              <span className="text-[#16d196]">investor specialist.</span>
            </h2>
            <p className="mt-5 text-[#0d1a2e]/65 text-[15.5px] lg:text-[16.5px] leading-relaxed">
              No call-centre, no pressure. Drop a note below or reach us directly —
              we typically reply within one business day.
            </p>

            <div className="mt-7 space-y-4">
              <a href={`mailto:${FOOTER.email}`} className="flex items-center gap-3 text-[#0d1a2e]/85 hover:text-[#16d196] group">
                <div className="w-10 h-10 rounded-xl bg-white border border-[#0d1a2e]/10 flex items-center justify-center group-hover:border-[#16d196]">
                  <Mail className="w-4 h-4 text-[#16d196]" />
                </div>
                <span className="text-[14.5px] font-semibold">{FOOTER.email}</span>
              </a>
              <a href={`tel:${FOOTER.phone}`} className="flex items-center gap-3 text-[#0d1a2e]/85 hover:text-[#16d196] group">
                <div className="w-10 h-10 rounded-xl bg-white border border-[#0d1a2e]/10 flex items-center justify-center group-hover:border-[#16d196]">
                  <Phone className="w-4 h-4 text-[#16d196]" />
                </div>
                <span className="text-[14.5px] font-semibold">{FOOTER.phone}</span>
              </a>
              <div className="flex items-start gap-3 text-[#0d1a2e]/85">
                <div className="w-10 h-10 rounded-xl bg-white border border-[#0d1a2e]/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#16d196]" />
                </div>
                <span className="text-[14.5px] font-semibold pt-2.5">{FOOTER.address}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white border border-[#0d1a2e]/10 rounded-3xl p-6 lg:p-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">Your name</label>
                  <input
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-2 w-full h-12 bg-[#f7faf9] border border-[#0d1a2e]/10 focus:border-[#16d196] outline-none rounded-xl px-4 text-[14.5px]"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">Email</label>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 w-full h-12 bg-[#f7faf9] border border-[#0d1a2e]/10 focus:border-[#16d196] outline-none rounded-xl px-4 text-[14.5px]"
                    placeholder="jane@email.com"
                  />
                </div>
              </div>
              <div>
                <label className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">How can we help?</label>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className="mt-2 w-full bg-[#f7faf9] border border-[#0d1a2e]/10 focus:border-[#16d196] outline-none rounded-xl px-4 py-3 text-[14.5px] resize-none"
                  placeholder="Tell us a bit about your goals…"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] rounded-full h-12 px-7 text-[14.5px] font-bold transition-colors disabled:opacity-50 shadow-[0_8px_24px_rgba(22,209,150,0.3)]"
              >
                {sending ? "Sending…" : "Send message"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
              </button>
              <p className="text-[11.5px] text-[#0d1a2e]/55">
                No spam. GDPR-secure. We never share or sell your details.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
